// React imports
import React, { useContext } from 'react';
import ReactDOM from 'react-dom/client';

// Import react libs
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

// Styles imports
import GlobalStyle from './utils/styles/GlobalStyle';

// Import contexts
import { AuthContext, AuthProvider } from './utils/contexts/AuthContext';
import { VisitProvider as ContextVisitProvider } from './utils/contexts/VisitContext';

// Import clients
import queryClient from './utils/query/queryClient';

// Page imports
import Home from './pages/Home';
import Product from './pages/Product/Index';
import About from './pages/About';
import Login from './pages/Login';
import Statistics from './pages/Statistics';
import Legal from './pages/Legal';
import Error404 from './pages/Error404';

// Import components
import PagesLayout from './components/PagesLayout';

// Icons and images sizes
const LOGO_WIDTH = 50;
const ICON_WIDTH = 30;

function VisitProvider({ children }) {
    const { isAdmin, isLoading } = useContext(AuthContext);

    if (isLoading) {
        return (
            <ContextVisitProvider isAdmin={false} isLoading={isLoading}>
                {children}
            </ContextVisitProvider>
        );
    }

    return (
        <ContextVisitProvider isAdmin={isAdmin} isLoading={isLoading}>
            {children}
        </ContextVisitProvider>
    );
}

// Root of the website
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <AuthProvider>
            <VisitProvider
                isAdmin={() => {
                    return useContext(AuthContext).isAdmin;
                }}
            >
                <QueryClientProvider client={queryClient}>
                    <BrowserRouter>
                        <GlobalStyle />
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    <PagesLayout
                                        LOGO_WIDTH={LOGO_WIDTH}
                                        ICON_WIDTH={ICON_WIDTH}
                                    >
                                        <Home ICON_WIDTH={ICON_WIDTH} />
                                    </PagesLayout>
                                }
                            />
                            <Route
                                path="/product/:id"
                                element={
                                    <PagesLayout
                                        LOGO_WIDTH={LOGO_WIDTH}
                                        ICON_WIDTH={ICON_WIDTH}
                                    >
                                        <Product
                                            ICON_WIDTH={ICON_WIDTH}
                                            mode="view"
                                        />
                                    </PagesLayout>
                                }
                            />
                            <Route
                                path="/product/new"
                                element={
                                    <PagesLayout
                                        LOGO_WIDTH={LOGO_WIDTH}
                                        ICON_WIDTH={ICON_WIDTH}
                                    >
                                        <Product
                                            ICON_WIDTH={ICON_WIDTH}
                                            mode="create"
                                        />
                                    </PagesLayout>
                                }
                            />
                            <Route
                                path="/product/edit/:id"
                                element={
                                    <PagesLayout
                                        LOGO_WIDTH={LOGO_WIDTH}
                                        ICON_WIDTH={ICON_WIDTH}
                                    >
                                        <Product
                                            ICON_WIDTH={ICON_WIDTH}
                                            mode="edit"
                                        />
                                    </PagesLayout>
                                }
                            />
                            <Route
                                path="/about"
                                element={
                                    <PagesLayout
                                        LOGO_WIDTH={LOGO_WIDTH}
                                        ICON_WIDTH={ICON_WIDTH}
                                    >
                                        <About ICON_WIDTH={ICON_WIDTH} />
                                    </PagesLayout>
                                }
                            />
                            <Route
                                path="/login"
                                element={
                                    <PagesLayout
                                        LOGO_WIDTH={LOGO_WIDTH}
                                        ICON_WIDTH={ICON_WIDTH}
                                    >
                                        <Login />
                                    </PagesLayout>
                                }
                            />
                            <Route
                                path="/statistics"
                                element={
                                    <PagesLayout
                                        LOGO_WIDTH={LOGO_WIDTH}
                                        ICON_WIDTH={ICON_WIDTH}
                                    >
                                        <Statistics />
                                    </PagesLayout>
                                }
                            />
                            <Route
                                path="/legal/legalnotices"
                                element={<Legal page="legal notices" />}
                            />
                            <Route
                                path="/legal/termesofuse"
                                element={<Legal page="termes of use" />}
                            />
                            <Route
                                path="/legal/privacypolicy"
                                element={<Legal page="privacy policy" />}
                            />
                            <Route path="*" element={<Error404 />} />
                        </Routes>
                    </BrowserRouter>
                </QueryClientProvider>
            </VisitProvider>
        </AuthProvider>
    </React.StrictMode>,
);
